import React, { useMemo, useState, useEffect } from "react";
import { Autocomplete } from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import { debounce } from "@mui/material/utils";
import { SearchResult } from "src/models/search";
import { ProductRow } from "src/pages/Messaging/components/ProductRow";
import { fetchProductionSearchAsync } from "src/infrastructure/http/appApiService";
import MuiTextField from "@mui/material/TextField";
import { styled } from "@mui/material/styles";

const AutoCompleteTextField = styled(MuiTextField)`
  .MuiInputBase-root {
    border-radius: 0;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  .MuiOutlinedInput-root {
    & fieldset {
      border-color: #ffe81f;
      border-width: 2px;
      box-shadow: 0 0 10px rgba(255, 225, 31, 0.5);
    }
    &:hover fieldset {
      border-color: #ffe81f;
      box-shadow: 0 0 15px rgba(255, 225, 31, 0.7);
    }
    &.Mui-focused fieldset {
      border-color: #ffe81f;
      box-shadow: 0 0 20px rgba(255, 225, 31, 0.9);
    }
  }
  .MuiInputBase-input {
    color: #ffe81f;
    font-family: "Courier New", monospace;
  }
  .MuiInputBase-input::placeholder {
    color: rgba(255, 225, 31, 0.6);
    opacity: 1;
  }
`;

type Props = {
  value: string;
  index: number;
  handleInputChange: (index: number, value: string, product?: SearchResult) => void;
  placeholder?: string;
};

export const ProductionSearchField: React.FC<Props> = ({ value, placeholder, handleInputChange, index }) => {
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchProducts = useMemo(
    () =>
      debounce(async (search: string, cb: (results?: SearchResult[]) => void) => {
        try {
          const res = await fetchProductionSearchAsync(search, 0, 10, "AU");
          cb(res.results);
        } catch {
          cb([]);
        }
      }, 400),
    []
  );

  useEffect(() => {
    if (!inputValue) return setOptions([]);

    setLoading(true);
    fetchProducts(inputValue, (results = []) => {
      setOptions(results);
      setLoading(false);
    });
  }, [inputValue, fetchProducts]);

  const getMatchedProduct = (options: SearchResult[], inputValue: string): SearchResult | null => {
    const search = inputValue.trim().toLowerCase();
    return (
      options.find(
        (opt) =>
          opt.sku?.toLowerCase() === search ||
          opt.barcode?.toLowerCase() === search ||
          opt.model?.toLowerCase() === search ||
          opt.description?.toLowerCase() === search
      ) || null
    );
  };

  return (
    <Autocomplete
      fullWidth
      autoSelect={false}
      autoHighlight={false}
      onKeyDown={(e) => {
        if (e.key === "Enter" && inputValue.trim()) {
          e.preventDefault();
          const matchedProduct = getMatchedProduct(options, inputValue);
          if (matchedProduct) {
            handleInputChange(index, matchedProduct.sku, matchedProduct);
          } else {
            handleInputChange(index, inputValue.trim());
          }
        }
      }}
      options={options}
      value={options.find((opt) => opt.sku === value) || null}
      getOptionLabel={() => ""}
      filterOptions={(options, { inputValue }) =>
        options.filter((opt) => {
          const search = inputValue.toLowerCase();
          return (
            opt.sku?.toLowerCase().includes(search) ||
            opt.barcode?.toLowerCase().includes(search) ||
            opt.model?.toLowerCase().includes(search) ||
            opt.description?.toLowerCase().includes(search)
          );
        })
      }
      isOptionEqualToValue={(opt, val) => opt.sku === val.sku}
      onInputChange={(_, newInput) => setInputValue(newInput)}
      renderInput={(params) => {
        const selectedProduct = options.find((opt) => opt.sku === value);
        return (
          <AutoCompleteTextField
            {...params}
            placeholder={placeholder || "Enter SKU, PLU, product name, etc."}
            fullWidth
            variant="outlined"
            size="small"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <SearchOutlined style={{ marginRight: 8, color: "#FFE81F" }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            InputLabelProps={{
              shrink: true,
            }}
            value={selectedProduct?.description || inputValue}
          />
        );
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.sku}>
          <ProductRow product={option} isAdded={false} onClick={() => handleInputChange(index, option.sku, option)} />
        </li>
      )}
      loading={loading}
      loadingText="Searching Production..."
      noOptionsText="No products found in Production"
    />
  );
};